import {
  Table,
  Card,
  Row,
  Col,
  Input,
  Select,
  DatePicker,
  Button,
  Modal,
  Tag,
  Typography,
  Space,
} from "antd";
import {
  SearchOutlined,
  InfoCircleOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import {
  RiCheckboxCircleFill,
  RiTimeFill,
  RiAlertFill,
  RiCloseCircleFill,
  RiErrorWarningFill,
} from "react-icons/ri";
import moment from "moment";
import "moment/locale/id";
import { useEffect, useState } from "react";

import MyPage from "../../../../../components/pages/Page";
import { useFetch } from "../../../../../hooks/useFetch";
import "../table-compact.css";

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const toYmd = (m) => (m ? m.format("YYYYMMDD") : "");

const fmtD = (v) => {
  if (!v && v !== 0) return "-";
  const m = moment(v, ["YYYYMMDD", "DD/MM/YYYY", "DD-MM-YYYY", "YYYY-MM-DD"], true);
  return m.isValid() ? m.format("DD/MM/YYYY") : String(v);
};

const parseDT = (v) => {
  if (!v && v !== 0) return null;
  const m = moment(
    v,
    ["YYYYMMDDHHmmss", "YYYYMMDD", "YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD"],
    true
  );
  return m.isValid() ? m : null;
};

const calcUmur = (r) => {
  const base =
    parseDT(r.tanggal_hawb) ||
    parseDT(r.hawb_date) ||
    parseDT(r.wk_in) ||
    parseDT(String(r.tanggal_dok_in ?? ""));
  if (!base) return "-";
  const diff = moment().diff(base.clone().startOf("day"), "days");
  if (diff < 0) return "-";
  if (diff > 30) return ">30 hari";
  return `${diff} hari`;
};

const buildQuery = ({ awb, range, page = 1, perPage = 5 }) => {
  const params = new URLSearchParams();
  if (awb) params.append("awb", awb);
  const [start, end] = range || [];
  if (start) params.append("start_date", toYmd(start));
  if (end) params.append("end_date", toYmd(end));
  params.append("page", page);
  params.append("perPage", perPage);
  return `api/v1/beacukai/currentnow?${params.toString()}`;
};

// respons API: { data: { data: [...], total, page, perPage, totalPage, statistic: [{label, value}] } }
const mapRow = (r, i) => {
  const inM = parseDT(r.wk_in) || parseDT(r.tanggal_dok_in);
  const outM = parseDT(r.wk_out) || parseDT(r.tanggal_dok_out);
  return {
    key: r.no_hawb ? `${r.no_hawb}-${i}` : i,
    statusBarang: r.status_barang ?? "-",
    umurBarang: calcUmur(r),
    noFlight: r.no_flight ?? r.flight_number ?? "-",
    noBC11: r.no_bc11 ?? "-",
    tglBC11: r.tanggal_bc11 ?? "-",
    noHAWB: r.no_hawb ?? r.hawb_number ?? "-",
    tglHAWB: r.tanggal_hawb ?? r.hawb_date ?? "-",
    consignee: r.consignee ?? r.exportir_name ?? "-",
    kodeDocIn: r.kode_dok_in ?? "-",
    noDocIn: r.nomor_dok_in ?? "-",
    jamDocIn: inM ? inM.format("HH:mm:ss") : "-",
    tglDocIn: inM ? inM.format("DD/MM/YYYY") : fmtD(r.tanggal_dok_in),
    kodeDocOut: r.kode_dok_out ?? "-",
    noDocOut: r.nomor_dok_out ?? "-",
    jamDocOut: outM ? outM.format("HH:mm:ss") : "-",
    tglDocOut: outM ? outM.format("DD/MM/YYYY") : fmtD(r.tanggal_dok_out),
    raw: r,
  };
};

const DEFAULT_RANGE = [moment().subtract(30, "days"), moment()];
const PER_PAGE = 5;

const dv = (v) => (v === null || v === undefined || v === "" ? "-" : String(v));

const Field = ({ label, value, required }) => (
  <div style={{ marginBottom: "12px" }}>
    <div style={{ fontSize: "11px", fontWeight: 600, color: "#334155", marginBottom: "4px" }}>
      {label}
      {required && <span style={{ color: "red" }}> *</span>}
    </div>
    <Input readOnly value={dv(value)} />
  </div>
);

const DetailModal = ({ data, open, onClose }) => {
  const t = String(data?.type || "").toUpperCase();
  return (
    <Modal
      title={<b>Detail Tracking &amp; Movement Barang</b>}
      open={open}
      onCancel={onClose}
      footer={null}
      width={640}
    >
      <div style={{ fontSize: "11px", fontWeight: 600, color: "#334155", marginBottom: "4px" }}>
        JENIS KEGIATAN<span style={{ color: "red" }}> *</span>
      </div>
      <Input
        readOnly
        value={t === "IMPORT" ? "Import" : t === "EXPORT" ? "Export" : "-"}
        style={{ width: "100%", marginBottom: "12px" }}
      />
      <div style={{ fontSize: "11px", color: "#64748b", marginBottom: "12px" }}>
        Pilih jenis kegiatan operasional barang (Import / Export).
      </div>

      <Field label="KD. TPS" value={data?.kd_tps ?? data?.kode_tps} required />
      <Row gutter={12}>
        <Col span={12}>
          <Field label="NAMA PENGANGKUT" value={data?.nama_pengangkut ?? data?.flight_number ?? data?.no_flight} required />
        </Col>
        <Col span={12}>
          <Field label="NO. VOY/FLIGHT" value={data?.flight_number ?? data?.no_flight} required />
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={12}>
          <Field label="TGL. TIBA / EKSPOR" value={data?.hawb_date ? fmtD(data.hawb_date) : fmtD(data?.tanggal_hawb)} required />
        </Col>
        <Col span={12}>
          <Field label="KD GUDANG" value={data?.kd_gudang ?? data?.kode_gudang ?? data?.kode_gudang_ekspor ?? data?.kode_gudang_impor} />
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={12}>
          <Field label="REF NUMBER IN" value={data?.reff_number_in} />
        </Col>
        <Col span={12}>
          <Field label="REF NUMBER OUT" value={data?.reff_number_out} />
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={12}>
          <Field label="NO. BL/AWB" value={data?.hawb_number ?? data?.no_hawb} required />
        </Col>
        <Col span={12}>
          <Field label="TGL. BL/AWB" value={data?.hawb_date ? fmtD(data.hawb_date) : fmtD(data?.tanggal_hawb)} required />
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={12}>
          <Field label="NO. MASTER BL/AWB" value={data?.mawb_number} />
        </Col>
        <Col span={12}>
          <Field label="TGL. MASTER BL/AWB" value={data?.mawb_date ? fmtD(data.mawb_date) : "-"} />
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={12}>
          <Field label="ID CONSIGNEE / NPWP" value={data?.exportir_npwp} />
        </Col>
        <Col span={12}>
          <Field label="CONSIGNEE / SHIPPER" value={data?.consignee ?? data?.exportir_name} required />
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={12}>
          <Field label="BRUTO (KG)" value={data?.bruto} />
        </Col>
        <Col span={12}>
          <div style={{ fontSize: "11px", fontWeight: 600, color: "#334155", marginBottom: "4px" }}>
            KODE &amp; JUMLAH KEMASAN
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <Input readOnly value={dv(data?.kode_kemas)} />
            <Input
              readOnly
              value={data?.jml_kemas === null || data?.jml_kemas === undefined ? "-" : `${data.jml_kemas} Kemasan`}
            />
          </div>
        </Col>
      </Row>
    </Modal>
  );
};

const STAT_CARDS = [
  { match: "1", bg: "#f6ffed", border: "#b7eb8f", color: "#389e0d", Icon: RiCheckboxCircleFill },
  { match: "6", bg: "#feffe6", border: "#fffb8f", color: "#d4b106", Icon: RiTimeFill },
  { match: "11", bg: "#fff7e6", border: "#ffd591", color: "#d46b08", Icon: RiAlertFill },
  { match: "21", bg: "#fff1f0", border: "#ffa39e", color: "#cf1322", Icon: RiCloseCircleFill },
  { match: ">30", bg: "rgba(207, 19, 34, 0.05)", border: "#ffa39e", color: "#820014", Icon: RiErrorWarningFill },
];

export default () => {
  const [range, setRange] = useState(DEFAULT_RANGE);
  const [awbInput, setAwbInput] = useState("");
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState(() =>
    buildQuery({ awb: "", range: DEFAULT_RANGE, page: 1, perPage: PER_PAGE })
  );
  const [rows, loading] = useFetch(query);
  const [lastUpdated, setLastUpdated] = useState(() => moment());
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (rows) setLastUpdated(moment());
  }, [rows]);

  const argsFor = (p) => ({
    awb: awbInput.trim(),
    range,
    page: p,
    perPage: PER_PAGE,
  });

  const doSearch = () => {
    setPage(1);
    setQuery(buildQuery(argsFor(1)));
  };

  const onRangeChange = (dates) => {
    setRange(dates);
    if (dates?.[0] && dates?.[1]) {
      setPage(1);
      setQuery(
        buildQuery({ awb: awbInput.trim(), range: dates, page: 1, perPage: PER_PAGE })
      );
    }
  };

  const handleTableChange = (pagination) => {
    const nextPage = pagination.current;
    setPage(nextPage);
    setQuery(buildQuery(argsFor(nextPage)));
  };

  const apiRows = Array.isArray(rows?.data) ? rows.data : [];
  const total = rows?.total ?? 0;
  const statistic = Array.isArray(rows?.statistic) ? rows.statistic : [];
  const dataSource = apiRows.map(mapRow);

  const statVal = (i, match) => {
    if (statistic[i] && typeof statistic[i].value !== "undefined") return statistic[i].value;
    const found = statistic.find((s) => String(s.label || "").includes(match));
    return found ? found.value : 0;
  };

  const columns = [
    {
      title: "Status Barang",
      dataIndex: "statusBarang",
      key: "statusBarang",
      render: (status) => {
        let color = "default";
        if (status === "Gate Out" || status === "Out") color = "green";
        else if (status === "Gate In" || (status && String(status).includes("In"))) color = "yellow";
        else if (status === "SPPB") color = "success";
        else if (status === "Hold Customs") color = "error";
        else if (status === "SP2MP") color = "warning";
        else if (status === "SPJM") color = "processing";
        else if (status === "Xray") color = "gold";
        return (
          <Tag color={color} style={{ fontWeight: 500 }}>
            ● {status}
          </Tag>
        );
      },
    },
    { title: "Umur Barang", dataIndex: "umurBarang", key: "umurBarang" },
    { title: "Nomor Flight", dataIndex: "noFlight", key: "noFlight" },
    { title: "Nomor BC11", dataIndex: "noBC11", key: "noBC11", render: (v) => v ?? "-" },
    {
      title: "Tanggal BC11",
      dataIndex: "tglBC11",
      key: "tglBC11",
      render: fmtD,
    },
    { title: "Nomor HAWB", dataIndex: "noHAWB", key: "noHAWB" },
    {
      title: "Tanggal HAWB",
      dataIndex: "tglHAWB",
      key: "tglHAWB",
      render: fmtD,
    },
    {
      title: "Consignee",
      dataIndex: "consignee",
      key: "consignee",
      render: (text) => <strong>{text}</strong>,
    },
    { title: "Kode Dokumen In", dataIndex: "kodeDocIn", key: "kodeDocIn" },
    {
      title: "Tanggal Dokumen In",
      dataIndex: "tglDocIn",
      key: "tglDocIn",
      render: (text, record) => (
        <div>
          {record.jamDocIn}
          <br />
          <span style={{ color: "#bfbfbf" }}>{record.tglDocIn}</span>
        </div>
      ),
    },
    {
      title: "Kode Dokumen Out",
      dataIndex: "kodeDocOut",
      key: "kodeDocOut",
      render: (text) => text || "-",
    },
    {
      title: "Nomor Dokumen Out",
      dataIndex: "noDocOut",
      key: "noDocOut",
      render: (text) => text || "-",
    },
    {
      title: "Tanggal Dokumen Out",
      dataIndex: "tglDocOut",
      key: "tglDocOut",
      render: (text, record) => (
        <div>
          {record.jamDocOut}
          <br />
          <span style={{ color: "#bfbfbf" }}>{record.tglDocOut}</span>
        </div>
      ),
    },
    {
      title: "History Movement dan Tracking Lastmile",
      dataIndex: "history",
      key: "history",
      render: (_, record) => (
        <a
          style={{ fontSize: "12px", textDecoration: "underline", color: "#1890ff" }}
          onClick={() => setDetail(record.raw)}
        >
          [Lihat Detail Movement]
        </a>
      ),
    },
  ];

  return (
    <MyPage title={"Current Now"}>
      <div
        className="dashboard-filters"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "12px",
          marginBottom: "20px",
          paddingBottom: "4px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "13px", color: "#1e293b", fontWeight: 500 }}>
            Filter Tampilan:
          </span>

          <Select
            defaultValue="harian"
            style={{ width: 100 }}
            size="small"
            options={[
              { value: "harian", label: "Harian" },
              { value: "mingguan", label: "Mingguan" },
            ]}
          />

          <Select
            defaultValue="bulanan"
            style={{ width: 105 }}
            size="small"
            options={[
              { value: "bulanan", label: "Bulanan" },
              { value: "tahunan", label: "Tahunan" },
            ]}
          />

          <RangePicker
            size="small"
            style={{ width: 350, flexShrink: 0 }}
            prefixIcon={
              <CalendarOutlined
                style={{ color: "#64748b", fontSize: "12px" }}
              />
            }
            suffixIcon={
              <CalendarOutlined
                style={{ color: "#64748b", fontSize: "12px" }}
              />
            }
            separator=" - "
            value={range}
            onChange={onRangeChange}
            format="DD MMMM YYYY"
          />
        </div>

        <div style={{ fontSize: "12px", color: "#475569" }}>
          Terakhir Diperbarui:{" "}
          <span style={{ fontWeight: 500 }}>
            {lastUpdated.clone().locale("id").format("DD MMMM YYYY, HH:mm [WIB]")}
          </span>
        </div>
      </div>

      <Row
        gutter={[12, 12]}
        justify="space-between"
        style={{ marginBottom: 24 }}
      >
        {STAT_CARDS.map((c, i) => (
          <Col xs={24} sm={12} md={4} key={c.match}>
            <Card
              size="small"
              style={{ background: c.bg, border: `1px solid ${c.border}` }}
            >
              <Row justify="space-between" align="middle">
                <Text strong style={{ color: c.color }}>
                  <c.Icon size={16} style={{ verticalAlign: "-3px", marginRight: 4 }} /> {statistic[i]?.label ?? ""}
                </Text>
                <InfoCircleOutlined style={{ color: "#bfbfbf" }} />
              </Row>
              <div style={{ marginTop: 8, fontSize: 12 }}>
                Jumlah HAWB: <strong>{statVal(i, c.match)}</strong>
              </div>
              <div style={{ marginTop: 4, fontSize: 10, color: "#8c8c8c" }}>
                Status Rincian di Bawah
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card
        size="small"
        style={{
          background: "#fafafa",
          border: "1px solid #e8e8e8",
          marginBottom: 24,
        }}
      >
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "#595959",
            marginBottom: 12,
          }}
        >
          <SearchOutlined /> Pencarian & Filter
        </div>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} lg={8}>
            <Space.Compact style={{ width: "100%" }}>
              <Input
                placeholder="Ketik Nomor MAWB atau HAWB..."
                style={{ width: "65%" }}
                value={awbInput}
                onChange={(e) => setAwbInput(e.target.value)}
                onPressEnter={doSearch}
                allowClear
              />
              <Select defaultValue="all" style={{ width: "35%" }}>
                <Option value="all">[Kategori M/HAWB V]</Option>
              </Select>
            </Space.Compact>
          </Col>

          <Col xs={24} md={12} lg={8}>
            <Row align="middle" gutter={8}>
              <Col>
                <Text style={{ fontSize: 12 }}>Time Gate In</Text>
              </Col>
              <Col flex="auto">
                <RangePicker
                  showTime
                  format="DD/MM/YYYY HH:mm"
                  style={{ width: "100%" }}
                />
              </Col>
            </Row>
          </Col>

          <Col xs={24} md={12} lg={8}>
            <Row align="middle" gutter={8}>
              <Col>
                <Text style={{ fontSize: 12 }}>Time Gate Out</Text>
              </Col>
              <Col flex="auto">
                <RangePicker
                  showTime
                  format="DD/MM/YYYY HH:mm"
                  style={{ width: "100%" }}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Card>

      <Card
        size="small"
        title={
          <span style={{ fontSize: 13, color: "#595959" }}>
            📄 Rincian Data Current Now & Pergerakan Barang
          </span>
        }
        bodyStyle={{ padding: 0 }}
        bordered
      >
        <Table
          className="bv2-compact"
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          pagination={{
            current: page,
            pageSize: PER_PAGE,
            total,
            showSizeChanger: false,
            showTotal: (t) => `Total ${t} data`,
          }}
          onChange={handleTableChange}
          size="small"
          scroll={{ x: "max-content" }}
          bordered
        />
      </Card>
      <DetailModal data={detail} open={!!detail} onClose={() => setDetail(null)} />
    </MyPage>
  );
};
